import React, { useEffect, useState, useRef } from 'react';
import EdgeMenu from './EdgeMenu';
import { UserIcon, LifeBuoyIcon, MenuIcon } from 'lucide-react';
interface LayoutProps {
  children: React.ReactNode;
  currentView: string;
  setCurrentView: (view: string) => void;
}
const Layout: React.FC<LayoutProps> = ({
  children,
  currentView,
  setCurrentView
}) => {
  const [menuVisible, setMenuVisible] = useState(false);
  const [edgeHovered, setEdgeHovered] = useState(false);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const mainRef = useRef<HTMLElement>(null);
  // Track scroll position for context-aware UI
  useEffect(() => {
    const handleScroll = () => {
      if (mainRef.current) {
        setScrollPosition(mainRef.current.scrollTop);
      }
    };
    const mainElement = mainRef.current;
    if (mainElement) {
      mainElement.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (mainElement) {
        mainElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);
  // Show edge indicator when mouse approaches left edge
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const edgeThreshold = 20; // pixels from the left edge
      if (e.clientX <= edgeThreshold) {
        setEdgeHovered(true);
      } else if (e.clientX > 50) {
        setEdgeHovered(false);
      }
    };
    // Touch events for swipe to open menu
    const handleTouchStart = (e: TouchEvent) => {
      setTouchStartX(e.touches[0].clientX);
    };
    const handleTouchMove = (e: TouchEvent) => {
      if (touchStartX !== null) {
        const touchX = e.touches[0].clientX;
        const diff = touchX - touchStartX;
        // If swiping from left edge to right
        if (touchStartX < 30 && diff > 50) {
          setMenuVisible(true);
          setTouchStartX(null);
        }
        // If swiping from right to left when menu is open
        if (menuVisible && touchStartX > window.innerWidth - 30 && diff < -50) {
          setMenuVisible(false);
          setTouchStartX(null);
        }
      }
    };
    const handleTouchEnd = () => {
      setTouchStartX(null);
    };
    // Custom event for navigating to therapist chat
    const handleNavigateToChat = () => {
      setCurrentView('therapist-chat');
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleTouchEnd);
    window.addEventListener('navigateToChat', handleNavigateToChat);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('navigateToChat', handleNavigateToChat);
    };
  }, [touchStartX, menuVisible, setCurrentView]);
  return <div className="relative w-full h-screen bg-white overflow-hidden" data-id="element-570">
      {/* Menu button with subtle pulse animation on load */}
      <button className="fixed left-4 top-4 w-12 h-12 rounded-full bg-white border-2 border-gray-300 flex items-center justify-center shadow-sm hover:bg-gray-100 active:bg-gray-200 transition-all duration-200 z-10 touch-manipulation animate-subtle-pulse" onClick={() => setMenuVisible(true)} aria-label="Open menu" data-id="element-571">
        <MenuIcon className="w-6 h-6 text-gray-700" data-id="element-572" />
      </button>
      {/* Edge indicator for menu activation */}
      <div className={`absolute left-0 top-0 h-full w-1 bg-gray-300 transition-opacity duration-300 ${edgeHovered ? 'opacity-100' : 'opacity-0'}`} data-id="element-573" />
      {/* Edge-sensitive area for menu activation */}
      <div className="absolute left-0 top-0 w-12 h-full cursor-pointer z-10 touch-manipulation" onMouseEnter={() => setEdgeHovered(true)} onClick={() => setMenuVisible(true)} data-id="element-574" />
      {/* Edge menu with improved animation */}
      <div className={`fixed left-0 top-0 h-full w-[85%] max-w-[320px] bg-white border-r border-gray-200 transform transition-transform duration-300 ease-out z-30 ${menuVisible ? 'translate-x-0' : '-translate-x-full'}`} data-id="element-575">
        <EdgeMenu currentView={currentView} setCurrentView={setCurrentView} onClose={() => setMenuVisible(false)} data-id="element-576" />
      </div>
      {/* Overlay to close menu when clicked outside */}
      {menuVisible && <div className="fixed inset-0 bg-black bg-opacity-10 backdrop-blur-[2px] z-20 touch-manipulation animate-fadeIn" onClick={() => setMenuVisible(false)} data-id="element-577" />}
      {/* Main content */}
      <main ref={mainRef} className="w-full h-screen overflow-y-auto p-4 pb-20 pt-20 scroll-smooth" data-id="element-578">
        {children}
      </main>
      {/* Floating help button with subtle hover effect */}
      
      {/* Profile button with subtle hover effect */}
      
      {/* Scroll indicator - appears when scrolled */}
      <div className={`fixed top-0 left-0 right-0 h-1 bg-gray-200 z-5 transition-opacity duration-300 ${scrollPosition > 50 ? 'opacity-100' : 'opacity-0'}`} data-id="element-579">
        <div className="h-full bg-gray-400" style={{
        width: `${Math.min(scrollPosition / (mainRef.current?.scrollHeight ? mainRef.current.scrollHeight - mainRef.current.clientHeight : 1) * 100, 100)}%`
      }} data-id="element-580" />
      </div>
    </div>;
};
export default Layout;